// BaseUploadPolicyService.js

import { getBaseEntitlements } from 'services/bases/BaseEntitlementsService';
import { BaseLimitError, BASE_LIMIT_CODES } from 'utilities/BaseLimitErrors';

function normalizePositiveInteger(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  const whole = Math.floor(parsed);
  if (whole < 1) return null;

  return whole;
}

function formatBytes(bytes) {
  const parsed = Number(bytes);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return '0 B';
  }

  if (parsed < 1024) {
    return `${Math.floor(parsed)} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = parsed;
  let unitIndex = -1;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[Math.max(unitIndex, 0)]}`;
}

export async function assertBaseUploadAllowed({ baseKey, fileName, fileSizeBytes }) {
  const normalizedSize = normalizePositiveInteger(fileSizeBytes);
  if (!normalizedSize) return;

  const cleanedBaseKey = String(baseKey || '').trim();
  if (!cleanedBaseKey) return;

  const entitlements = await getBaseEntitlements({ baseKey: cleanedBaseKey });
  const maxUploadBytes = normalizePositiveInteger(entitlements?.features?.maxUploadBytes);
  const effectiveStorageBytes = normalizePositiveInteger(entitlements?.effective?.storageBytes);
  const storageBytesUsed = Math.max(0, Math.floor(Number(entitlements?.usage?.storageBytesUsed || 0)));
  const storageBytesReserved = Math.max(0, Math.floor(Number(entitlements?.usage?.storageBytesReserved || 0)));
  const storageEffective = storageBytesUsed + storageBytesReserved;

  if (maxUploadBytes && normalizedSize > maxUploadBytes) {
    const normalizedFileName = String(fileName || '').trim();
    const label = normalizedFileName ? `"${normalizedFileName}"` : 'This file';
    throw new BaseLimitError(
      `${label} exceeds this base plan upload limit (${formatBytes(maxUploadBytes)}).`,
      {
        code: BASE_LIMIT_CODES.FILE_SIZE,
        limit: maxUploadBytes,
        limitType: 'file_size',
        unit: 'bytes',
      }
    );
  }

  if (effectiveStorageBytes && (storageEffective + normalizedSize) > effectiveStorageBytes) {
    throw new BaseLimitError(
      `This space is out of storage (${formatBytes(storageEffective)} used of ${formatBytes(effectiveStorageBytes)}).`,
      {
        code: BASE_LIMIT_CODES.STORAGE,
        limit: effectiveStorageBytes,
        limitType: 'storage',
        unit: 'bytes',
        used: storageEffective,
      }
    );
  }
}
